import { Badge } from '@mantine/core'
import type { Icon as TablerIcon } from '@tabler/icons-react'
import {
  IconClock, IconCheck, IconX, IconAlertCircle,
  IconSearch, IconPlayerPlay, IconHourglass,
  IconCircleCheck, IconLock, IconRefresh,
} from '@tabler/icons-react'

export type ChamadoStatus =
  | 'requested' | 'pending_approval' | 'approved' | 'rejected'
  | 'in_triage' | 'in_progress' | 'waiting_customer'
  | 'blocked' | 'pending_customer_validation'
  // Fluxo de mudança por ambiente (change_workflow_service): execução → validação
  // técnica → aceite → promoção.
  | 'pending_validation' | 'pending_signoff' | 'ready_to_promote'
  | 'resolved' | 'closed' | 'reopened'
  // GMUD (gestão de mudanças) — mesmo componente humaniza os status do fluxo GMUD.
  | 'gmud_draft' | 'gmud_submitted' | 'gmud_technical_review' | 'gmud_pending_cab'
  | 'gmud_approved' | 'gmud_scheduled' | 'gmud_executing' | 'gmud_completed'
  | 'gmud_failed' | 'gmud_rolled_back' | 'gmud_ratification' | 'gmud_closed'

// Rótulos por extenso, do ponto de vista do atendimento (spec 2026-10-19 Entrada Mailbox §6).
// É o rótulo NEUTRO: filtros e relatórios não têm "quem olha". O rótulo relativo a quem olha
// e o "com quem" saem de `rotuloDoStatus(status, ctx)`, só na Entrada e no detalhe.
const STATUS_CONFIG: Record<ChamadoStatus, { color: string; label: string; Icon: TablerIcon }> = {
  requested:        { color: 'blue',   label: 'Aguardando triagem',     Icon: IconClock },
  pending_approval: { color: 'yellow', label: 'Aguardando aprovação',   Icon: IconHourglass },
  approved:         { color: 'green',  label: 'Aprovado',               Icon: IconCheck },
  rejected:         { color: 'red',    label: 'Rejeitado',              Icon: IconX },
  // Neutro diferente de `requested`: sem saber se há `triagem_iniciada`, o filtro não pode
  // oferecer duas opções "Aguardando triagem". Com ctx vira "Aguardando triagem" ou
  // "Triagem em execução" (§6.1).
  in_triage:        { color: 'blue',   label: 'Em triagem',             Icon: IconSearch },
  in_progress:      { color: 'indigo', label: 'Em atendimento',         Icon: IconPlayerPlay },
  waiting_customer: { color: 'yellow', label: 'Aguardando o cliente',   Icon: IconHourglass },
  blocked:          { color: 'blue.7', label: 'Bloqueado',              Icon: IconLock },
  pending_customer_validation: { color: 'grape', label: 'Aguardando validação do cliente', Icon: IconCircleCheck },
  // Validação técnica é de quem tem gestão de mudança, não do cliente: rótulo próprio.
  pending_validation: { color: 'grape', label: 'Aguardando validação técnica', Icon: IconSearch },
  pending_signoff:    { color: 'grape', label: 'Aguardando aceite',            Icon: IconHourglass },
  ready_to_promote:   { color: 'green', label: 'Pronto para promover',         Icon: IconCheck },
  resolved:         { color: 'teal',   label: 'Resolvido',              Icon: IconCircleCheck },
  closed:           { color: 'gray',   label: 'Fechado',                Icon: IconLock },
  reopened:         { color: 'orange', label: 'Reaberto',               Icon: IconRefresh },
  // GMUD — por extenso, sem sigla, a partir do rótulo que já existia.
  gmud_draft:            { color: 'gray',   label: 'Rascunho',                       Icon: IconClock },
  gmud_submitted:        { color: 'blue',   label: 'Submetida',                      Icon: IconHourglass },
  gmud_technical_review: { color: 'cyan',   label: 'Revisão técnica',                Icon: IconSearch },
  gmud_pending_cab:      { color: 'yellow', label: 'Aguardando o comitê de mudança', Icon: IconHourglass },
  gmud_approved:         { color: 'green',  label: 'Aprovada',                       Icon: IconCheck },
  gmud_scheduled:        { color: 'indigo', label: 'Agendada',                       Icon: IconClock },
  gmud_executing:        { color: 'indigo', label: 'Em execução',                    Icon: IconPlayerPlay },
  gmud_completed:        { color: 'teal',   label: 'Concluída',                      Icon: IconCircleCheck },
  gmud_failed:           { color: 'red',    label: 'Falhou',                         Icon: IconX },
  gmud_rolled_back:      { color: 'orange', label: 'Revertida',                      Icon: IconRefresh },
  gmud_ratification:     { color: 'grape',  label: 'Em ratificação',                 Icon: IconHourglass },
  gmud_closed:           { color: 'gray',   label: 'Encerrada',                      Icon: IconLock },
}

/** A cor do badge do status (a tela "Como funciona" pinta o estado com a mesma cor). */
export function corDoStatus(status: string): string {
  return STATUS_CONFIG[status as ChamadoStatus]?.color ?? 'gray'
}

/** Todo status que o badge rotula, na ordem da tabela. */
export const STATUS_DO_CHAMADO = Object.keys(STATUS_CONFIG) as ChamadoStatus[]

/** Quem olha um chamado (Entrada e detalhe) e o que o rótulo precisa para dizer "com quem". */
export interface ContextoDoRotulo {
  visao: 'solicitante' | 'atendimento'
  /** Primeiro nome de quem iniciou a triagem nesta chegada (`triagem_iniciada_por_nome`). */
  triagemIniciadaPor?: string | null
  /** A triagem foi concluída nesta chegada (`triagem_concluida`): "Aguardando atendimento". */
  triagemConcluida?: boolean
  responsavel?: string | null
  fila?: string | null
  /** O chamado voltou a `requested` por um encaminhamento. */
  encaminhado?: boolean
}

const comQuem = (rotulo: string, quem: string | null | undefined) =>
  quem ? `${rotulo} · ${quem}` : rotulo

interface StatusBadgeProps {
  status: ChamadoStatus | string
  size?: 'xs' | 'sm' | 'md'
  withIcon?: boolean
  /** Só na Entrada e no detalhe: o rótulo passa a dizer com quem o chamado está. */
  ctx?: ContextoDoRotulo
}

export function StatusBadge({ status, size = 'sm', withIcon = false, ctx }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status as ChamadoStatus] ?? { color: 'gray', label: status, Icon: IconAlertCircle }
  const variant = status === 'closed' ? 'filled' : 'light'
  const Icon = config.Icon
  const label = rotuloDoStatus(status, ctx)

  return (
    <Badge
      color={config.color}
      variant={variant}
      size={size}
      leftSection={withIcon ? <Icon size={10} /> : undefined}
      aria-label={`Status: ${label}`}
    >
      {label}
    </Badge>
  )
}

/**
 * Rótulo humano de um status — a tabela inteira mora aqui (spec Entrada Mailbox §6).
 * Sem `ctx`: o rótulo neutro, do atendimento, para filtros e relatórios. Com `ctx`: o
 * rótulo de quem olha e "com quem" o chamado está.
 */
export function rotuloDoStatus(status: string, ctx?: ContextoDoRotulo): string {
  const neutro = STATUS_CONFIG[status as ChamadoStatus]?.label ?? status
  if (!ctx) return neutro
  const fila = ctx.fila ? `fila ${ctx.fila}` : null
  switch (status) {
    case 'requested':
      if (ctx.encaminhado) {
        return `${ctx.fila ? `Encaminhado para ${ctx.fila}` : 'Encaminhado'} · aguardando triagem`
      }
      return comQuem('Aguardando triagem', fila)
    case 'in_triage':
      // Spec 2026-10-21 §4.6: concluída, o chamado espera alguém do time pegar.
      if (ctx.triagemConcluida) {
        return ctx.visao === 'solicitante' ? 'Aguardando atendimento' : comQuem('Aguardando atendimento', fila)
      }
      return ctx.triagemIniciadaPor
        ? comQuem('Triagem em execução', ctx.triagemIniciadaPor)
        : comQuem('Aguardando triagem', fila)
    case 'in_progress':
    case 'blocked':
      return comQuem(neutro, ctx.responsavel)
    case 'reopened':
      return comQuem(neutro, ctx.responsavel || 'aguardando triagem')
    case 'waiting_customer':
      return ctx.visao === 'solicitante' ? 'Aguardando você' : neutro
    case 'pending_customer_validation':
      return ctx.visao === 'solicitante' ? 'Aguardando sua validação' : neutro
    default:
      return neutro
  }
}

/** Opções de `Select` para os status pedidos, na ordem pedida, com o rótulo do badge. */
export function opcoesDeStatus(statuses: readonly string[]): { value: string; label: string }[] {
  return statuses.map((s) => ({ value: s, label: rotuloDoStatus(s) }))
}

/**
 * Filtro da lista de chamados: TODO status que o STATUS_CONFIG rotula, em dois grupos.
 * Derivado, e não copiado, para o status que uma onda acrescentar ao badge aparecer no
 * filtro sem ninguém lembrar (spec 2026-09-27-chamados-revisao-geral §7).
 */
export function opcoesDoFiltroDeStatus(): { group: string; items: { value: string; label: string }[] }[] {
  const todos = Object.keys(STATUS_CONFIG)
  return [
    { group: 'Chamado', items: opcoesDeStatus(todos.filter((s) => !s.startsWith('gmud_'))) },
    { group: 'GMUD', items: opcoesDeStatus(todos.filter((s) => s.startsWith('gmud_'))) },
  ]
}
